import { FirebasePerformanceTypes } from '@react-native-firebase/perf';
import { omit } from 'lodash-es';

import { appVersion } from 'utils/app-version-info';
import { getConfigValue, isWeb } from 'utils/environment';
import { SyncStorageKey, syncStorage } from 'utils/environment/sync-storage';
import noop from 'utils/noop';

import { getApp, getPerformance, trace } from './firebase-perf-wrapper';
import { TraceName } from './types';
export { TraceName } from './types';

const MAX_TRACE_COUNT = 250;

const traces = new Map<string, FirebasePerformanceTypes.Trace>();

const ALLOW_DEV_ONLY_DEBUG_TRACES = !getConfigValue({ key: 'disableDevTraceLogging' }) && __DEV__;

/**
 * start a trace with a string name
 * be mindful of a few things:
 * - it wont throw errors or log them
 * - if you use the same name twice you better keep the returned trace reference
 * @param traceName the name of the trace as it will appear in the firebase performance tab
 * @param additionalMetadata additional metadata that will show up per trace instance, (up to 4, rest discarded!)
 * @returns the trace that can be used for stopping it (or use stopTrace if you just have the name)
 */
export const startTrace = (
  traceName: string | TraceName,
  additionalMetadata: Record<string, string> = {}
) => {
  try {
    const traceInstance = trace(getPerformance(getApp()), traceName);
    if (ALLOW_DEV_ONLY_DEBUG_TRACES) {
      (traceInstance as any).startTime = new Date().getTime();
    }

    const metadata = {
      userId: syncStorage.getString(SyncStorageKey.USER_ID),
      version: appVersion,
      ...additionalMetadata,
    };
    // filter for rules of attributes and non empty
    Object.entries(metadata)
      .filter(([, v]) => !!v)
      .slice(0, 5) // max of 5 attributes on a trace
      .forEach(([k, v]) => traceInstance.putAttribute(k, v!));

    // prune traces on `stop` and preserve `this` context with bind
    const origStop = traceInstance.stop.bind(traceInstance);
    if (__DEV__) {
      // required for dev only logging of metadata
      (traceInstance as any)._metadata = metadata;
    }
    traceInstance.stop = async () => {
      traces.delete(traceName);

      if (ALLOW_DEV_ONLY_DEBUG_TRACES) {
        DEV_ONLY_colorLog(traceName, traceInstance);
      }

      return origStop().catch(() => null); // oh well
    };

    // prevent a memory leak situation that likely can't happen but...
    if (traces.size > MAX_TRACE_COUNT) {
      const oldestKey = traces.keys().next().value;
      oldestKey && traces.delete(oldestKey);
    }

    traces.set(traceName, traceInstance);
    traceInstance.start().catch(noop);
    return traceInstance;
  } catch (_err) {}
  return null;
};

/**
 * stop a trace by name - safe to call even if it doesn't exist
 */
export const stopTrace = async (
  traceName: string | TraceName,
  additionalMetadata: Record<string, string> = {}
) => {
  try {
    const traceInstance = traces.get(traceName);
    if (!traceInstance) {
      return null;
    }
    Object.entries(additionalMetadata)
      .filter(([, v]) => !!v)
      .slice(0, 3) // max of 5 attributes on a trace, 2 already added likely
      .forEach(([k, v]) => traceInstance.putAttribute(k, v!));

    if (__DEV__) {
      // required for dev only logging of metadata
      (traceInstance as any)._metadata = {
        ...((traceInstance as any)._metadata || {}),
        ...additionalMetadata,
      };
    }
    return traceInstance.stop();
  } catch (_err) {}
  return null;
};

const devOnlyDupeTracker: string[] = [];
// in __DEV__ only:
// - log colors on a gradient based on seconds 0 green -> 1 yellow -> 2 red
// - log duplicate (within X seconds) traces
function DEV_ONLY_colorLog(traceName: string, trace: FirebasePerformanceTypes.Trace) {
  const timeSeconds = (new Date().getTime() - (trace as any).startTime) / 1000;
  devOnlyDupeTracker.push(traceName);
  let dupeAlert = '';
  const arrayCount = devOnlyDupeTracker.filter(x => x === traceName).length;
  if (arrayCount > 1) {
    dupeAlert = ` \x1b[37m ⚠️ (x${arrayCount} traces recently!) \x1b[0m`;
  }
  setTimeout(() => {
    // remove the traceName from the dupe tracking array after the timeout
    devOnlyDupeTracker.splice(devOnlyDupeTracker.indexOf(traceName), 1);
  }, 5000);

  const clampedValue = Math.max(0, Math.min(2, timeSeconds));
  const metadata = JSON.stringify(omit((trace as any)._metadata, ['userId', 'version']));
  const message = `TRACE END - ${timeSeconds.toFixed(
    2
  )}s - ${traceName}${dupeAlert} ${new Date().toISOString()} ${metadata === '{}' ? '' : metadata}`;

  if (isWeb) {
    let style = '';
    if (clampedValue <= 1) {
      const red = Math.round((clampedValue / 1) * 255);
      style = `color: rgb(${red}, 255, 0);`;
    } else {
      const green = 255 - Math.round(((clampedValue - 1) / 1) * 255);
      style = `color: rgb(255, ${green}, 0);`;
    }
    // eslint-disable-next-line no-console
    console.log(`%c${message}`, style);
  } else {
    // mobile terminal
    let colorCode: number;
    if (clampedValue <= 1) {
      const redScaled = Math.round((clampedValue / 1) * 5);
      colorCode = 16 + 36 * redScaled + 6 * 5;
    } else {
      const greenScaled = 5 - Math.round(((clampedValue - 1) / 1) * 5);
      colorCode = 16 + 36 * 5 + 6 * greenScaled;
    }
    // eslint-disable-next-line no-console
    console.log(`\x1b[38;5;${colorCode}m${message}\x1b[0m`);
  }
}
